import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Crown, Award, Medal, Sparkles, MapPin, Layers, Save, Gift, Flame, ArrowRight, TrendingUp } from "lucide-react";

const TIER_ICON = { free: Gift, bronze: Medal, silver: Award, gold: Crown };
const TIER_LABEL = { free: "Gratuit", bronze: "Bronze", silver: "Argent", gold: "Or" };

export default function Dashboard() {
  const { t, lang } = useLang();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ sectors: [], locations: [] });
  const [sub, setSub] = useState(null);
  const [usage, setUsage] = useState(0);
  const [usedWeek, setUsedWeek] = useState(0);
  const [missedWeek, setMissedWeek] = useState(0);
  const [sel, setSel] = useState({ sectors: [], locations: [] });
  const [myReqs, setMyReqs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [locQuery, setLocQuery] = useState("");
  const normTxt = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  const loadSub = async () => {
    const r = await api.get("/subscription");
    setSub(r.data.subscription);
    setUsage(r.data.usage);
    setUsedWeek(r.data.usage_week || 0);
    setMissedWeek(r.data.missed_week || 0);
    if (r.data.subscription) {
      setSel({ sectors: r.data.subscription.pref_sectors || [], locations: r.data.subscription.pref_locations || [] });
    }
  };

  useEffect(() => {
    if (!user) return;
    api.get("/meta").then((r) => setMeta(r.data));
    loadSub().catch(() => {});
    api.get("/requests/mine", { params: { email: user.email } }).then((r) => setMyReqs(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [user]);

  if (loading || !user) return null;
  if (!sub) return <div className="max-w-6xl mx-auto px-4 py-20 text-center text-muted-foreground">…</div>;

  const tierData = sub.limits;
  const isFree = sub.tier === "free";
  const Icon = TIER_ICON[sub.tier] || Gift;

  const toggle = (kind, val) => {
    setSel((prev) => {
      const arr = prev[kind];
      const max = kind === "sectors" ? tierData.sectors : tierData.locations;
      let next;
      if (arr.includes(val)) next = arr.filter((x) => x !== val);
      else {
        if (max < 99 && arr.length >= max) { toast.error(lang === "fr" ? `Limite: ${max}` : `Limit: ${max}`); return prev; }
        next = [...arr, val];
      }
      return { ...prev, [kind]: next };
    });
  };

  const savePrefs = async () => {
    setSaving(true);
    try {
      await api.post("/subscription/preferences", sel);
      toast.success(t("save_prefs"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const quota = isFree ? (tierData.leads_per_week ?? 1) : (tierData.leads_per_month ?? 0);
  const shownUsage = isFree ? usedWeek : usage;
  const pct = isFree
    ? Math.min((shownUsage / quota) * 100, 100)
    : (quota >= 99 ? Math.min((usage / 50) * 100, 100) : Math.min((usage / quota) * 100, 100));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t("dash_title")}</h1>
        <Button variant="outline" data-testid="dash-edit-profile-btn" onClick={() => navigate("/profile/edit")}>
          <Sparkles className="h-4 w-4 mr-1.5" /> {t("edit_profile")}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* Plan card */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">{t("dash_plan")}</p>
          <div className="flex items-center gap-3 mb-4">
            <div className="grid place-items-center h-11 w-11 rounded-xl bg-primary/10 text-primary">
              <Icon className="h-6 w-6" />
            </div>
            <div>
              <div className="font-display text-2xl font-bold" data-testid="dash-tier-name">{TIER_LABEL[sub.tier] || "Gratuit"}</div>
              <Badge variant="secondary" className="rounded-full text-xs">{isFree ? t("free_name") : (lang === "fr" ? "Actif" : "Active")}</Badge>
            </div>
          </div>
          <Button className={`w-full ${isFree ? "" : ""}`} variant={isFree ? "default" : "outline"} size="sm"
            data-testid="dash-upgrade-btn" onClick={() => navigate("/pricing")}>
            <Sparkles className="h-4 w-4 mr-1.5" /> {isFree ? t("upgrade_plan") : (lang === "fr" ? "Changer de forfait" : "Change plan")}
          </Button>
        </div>

        {/* Usage card */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm lg:col-span-2">
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">{isFree ? t("dash_usage_week") : t("dash_usage")}</p>
          <div className="flex items-end gap-2 mb-4">
            <span className="font-display text-4xl font-extrabold" data-testid="dash-usage-count">{shownUsage}</span>
            <span className="text-muted-foreground mb-1.5">/ {quota >= 99 ? t("unlimited") : quota}</span>
          </div>
          <Progress value={pct} className="h-2" />
          <p className="text-sm text-muted-foreground mt-4">
            {isFree ? t("free_hint") : (lang === "fr" ? "Débloquez plus de demandes en montant de forfait." : "Unlock more requests by upgrading.")}
          </p>
        </div>
      </div>

      {/* Missed contacts (free upsell) */}
      {isFree && missedWeek > 0 && (
        <div data-testid="missed-contacts-card"
          className="relative overflow-hidden rounded-2xl bg-primary text-primary-foreground p-6 sm:p-8 shadow-sm mb-8">
          <div className="absolute inset-0 grid-dots opacity-10" />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <div className="flex items-start gap-4">
              <div className="grid place-items-center h-12 w-12 rounded-xl bg-primary-foreground/15 shrink-0">
                <Flame className="h-6 w-6" />
              </div>
              <div>
                <h2 className="font-display text-xl font-bold">{t("missed_title")}</h2>
                <p className="text-primary-foreground/80 mt-1">
                  <span data-testid="missed-count" className="font-display text-3xl font-extrabold align-middle mr-1.5">{missedWeek}</span>
                  {t("missed_label")}
                </p>
              </div>
            </div>
            <Button variant="secondary" size="lg" className="shrink-0" data-testid="missed-upgrade-btn"
              onClick={() => navigate("/pricing")}>
              {t("missed_cta")} <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </div>
        </div>
      )}

      {/* Captured this month (paid valorization) */}
      {!isFree && (
        <div data-testid="captured-card"
          className="relative overflow-hidden rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 sm:p-8 shadow-sm mb-8">
          <div className="flex items-center gap-4">
            <div className="grid place-items-center h-12 w-12 rounded-xl bg-emerald-500/15 text-emerald-600 shrink-0">
              <TrendingUp className="h-6 w-6" />
            </div>
            <div>
              <h2 className="font-display text-xl font-bold">{t("captured_title")}</h2>
              <p className="text-muted-foreground mt-0.5">
                <span data-testid="captured-count" className="font-display text-3xl font-extrabold text-emerald-600 align-middle mr-1.5">{usage}</span>
                {t("captured_label")}
              </p>
              <p className="text-sm text-muted-foreground mt-1">{t("captured_sub")}</p>
            </div>
          </div>
        </div>
      )}

      {/* Preferences */}
      {sub && !isFree && (
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-sm mb-8">
          <div className="flex items-center justify-between gap-4 mb-2">
            <h2 className="font-display text-xl font-semibold">{t("dash_prefs")}</h2>
            <Button size="sm" onClick={savePrefs} disabled={saving} data-testid="save-prefs-btn">
              <Save className="h-4 w-4 mr-1.5" /> {saving ? t("processing") : t("save_prefs")}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mb-6">{t("dash_prefs_sub")}</p>

          <div className="mb-6">
            <p className="text-sm font-medium mb-3 inline-flex items-center gap-2">
              <Layers className="h-4 w-4 text-muted-foreground" /> {t("f_sector")}
              <span className="text-xs text-muted-foreground">({sel.sectors.length}/{tierData.sectors >= 99 ? "∞" : tierData.sectors})</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {meta.sectors.map((s) => (
                <button key={s.id} data-testid={`pref-sector-${s.id}`} onClick={() => toggle("sectors", s.id)}
                  className={`rounded-full border px-3.5 py-1.5 text-sm transition-all ${
                    sel.sectors.includes(s.id) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"
                  }`}>
                  {lang === "fr" ? s.fr : s.en}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm font-medium mb-3 inline-flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" /> {t("f_location")}
              <span className="text-xs text-muted-foreground">({sel.locations.length}/{tierData.locations >= 99 ? "∞" : tierData.locations})</span>
            </p>
            <div className="mb-3">
              <Input value={locQuery} onChange={(e) => setLocQuery(e.target.value)}
                placeholder={lang === "fr" ? "Rechercher une ville…" : "Search a city…"}
                data-testid="pref-location-search" className="h-9 max-w-xs" />
            </div>
            <div className="space-y-4 max-h-96 overflow-y-auto pr-1">
              {(meta.location_groups || [])
                .map((g) => ({ region: g.region, cities: locQuery ? g.cities.filter((c) => normTxt(c).includes(normTxt(locQuery))) : g.cities }))
                .filter((g) => g.cities.length)
                .map((g) => (
                <div key={g.region}>
                  <p className="text-xs font-semibold text-primary/70 uppercase tracking-wide mb-2">{g.region}</p>
                  <div className="flex flex-wrap gap-2">
                    {g.cities.map((l) => (
                      <button key={l} data-testid={`pref-location-${l}`} onClick={() => toggle("locations", l)}
                        className={`rounded-full border px-3.5 py-1.5 text-sm transition-all ${
                          sel.locations.includes(l) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"
                        }`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* My posted requests */}
      {myReqs.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-sm">
          <h2 className="font-display text-xl font-semibold mb-5">{t("my_requests")}</h2>
          <div className="space-y-3">
            {myReqs.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-4 rounded-xl border border-border p-4">
                <div>
                  <p className="font-medium">{r.title}</p>
                  <p className="text-xs text-muted-foreground">{r.location}</p>
                </div>
                <Badge variant="secondary" className="rounded-full">{r.status}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
